/**
 * Payable tranches must add up to what the creator is owed (net − deductions).
 * Saved tranches can be out of date — e.g. a deduction added after the
 * tranches were split — so on opening a campaign the unpaid ones are
 * re-split to close the gap. Paid tranches are history and never change.
 */
export interface PayableLike {
  amount: number;
  status?: string;
}

export interface PayableAdjustment {
  index: number;
  from: number;
  to: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function reconcilePayables<T extends PayableLike>(
  invoices: T[],
  target: number
): { invoices: T[]; adjustments: PayableAdjustment[] } | null {
  if (invoices.length === 0 || !(target >= 0)) return null;

  const total = round2(invoices.reduce((s, inv) => s + (inv.amount || 0), 0));
  if (Math.abs(total - round2(target)) < 0.01) return null;

  const unpaid = invoices.map((inv, i) => ({ inv, i })).filter(({ inv }) => inv.status !== 'Paid');
  if (unpaid.length === 0) return null;

  const paidSum = invoices
    .filter(inv => inv.status === 'Paid')
    .reduce((s, inv) => s + (inv.amount || 0), 0);
  const remaining = Math.max(0, round2(target - paidSum));
  const share = round2(remaining / unpaid.length);
  const lastIndex = unpaid[unpaid.length - 1].i;
  const lastShare = round2(remaining - share * (unpaid.length - 1));

  const adjustments: PayableAdjustment[] = [];
  const next = invoices.map((inv, i) => {
    if (inv.status === 'Paid') return inv;
    const to = i === lastIndex ? lastShare : share;
    if (Math.abs((inv.amount || 0) - to) < 0.01) return inv;
    adjustments.push({ index: i, from: inv.amount || 0, to });
    return { ...inv, amount: to };
  });

  return adjustments.length > 0 ? { invoices: next, adjustments } : null;
}
